import React, { useRef } from "react";

const BAR_HEIGHT = 22;
const LONG_PRESS_MS = 500;

const MonthView = ({
  currentDate,
  events,
  getEventsForDate,
  getColorForEvent,
  onNavigateToDay,
  onOpenNewEvent,
  onOpenEditEvent,
  weekStartsMonday = false,
}) => {
  const longPressTimer = useRef(null);
  const longPressFired = useRef(false);
  const touchMovedRef = useRef(false);

  const handleCellTouchStart = (date, e) => {
    longPressFired.current = false;
    touchMovedRef.current = false;
    longPressTimer.current = setTimeout(() => {
      if (touchMovedRef.current) return;
      longPressFired.current = true;
      if (navigator.vibrate) navigator.vibrate(50);
      onOpenNewEvent(date);
    }, LONG_PRESS_MS);
  };

  const handleCellTouchMove = () => {
    touchMovedRef.current = true;
    clearTimeout(longPressTimer.current);
  };

  const handleCellTouchEnd = (date, e) => {
    clearTimeout(longPressTimer.current);
    if (!longPressFired.current && !touchMovedRef.current) {
      onNavigateToDay(date);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    // Adjust startingDayOfWeek for Monday start
    const rawDay = firstDay.getDay(); // 0=Sun ... 6=Sat
    const startingDayOfWeek = weekStartsMonday ? (rawDay + 6) % 7 : rawDay;
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const parseDate = (str) => {
    const [y, m, d] = str.split("-").map(Number);
    return new Date(y, m - 1, d);
  };

  const dateToStr = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);

  // Day header order
  const dayHeaders = weekStartsMonday
    ? ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
    : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Build cells (including prev/next month padding)
  const cells = [];
  for (let i = 0; i < startingDayOfWeek; i++) {
    const d = new Date(year, month, 1 - (startingDayOfWeek - i));
    cells.push({ date: d, inMonth: false });
  }
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push({ date: new Date(year, month, day), inMonth: true });
  }
  while (cells.length % 7 !== 0) {
    const d = new Date(year, month + 1, cells.length - (startingDayOfWeek + daysInMonth) + 1);
    cells.push({ date: d, inMonth: false });
  }

  // Split into weeks
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }

  // Multi-day events
  const multiDayEvents = (events || []).filter((e) => e.end_date && e.end_date > e.date);

  const today = new Date();

  return (
    <div className="border-b border-l border-gray-200">
      {/* Day headers */}
      <div className="grid grid-cols-7 mb-1">
        {dayHeaders.map((day) => (
          <div key={day} className="text-center font-semibold text-gray-600 py-2 text-xs sm:text-sm">
            <span className="hidden sm:inline">{day}</span>
            <span className="sm:hidden">{day.substring(0, 1)}</span>
          </div>
        ))}
      </div>

      {/* Week rows */}
      {weeks.map((week, weekIdx) => {
        const weekStartStr = dateToStr(week[0].date);
        const weekEndStr = dateToStr(week[6].date);

        // Multi-day events visible in this week
        const weekMultiDay = multiDayEvents.filter(
          (e) => e.date <= weekEndStr && e.end_date >= weekStartStr
        );

        // Compute bar positions
        const bars = weekMultiDay.map((event) => {
          const clampedStart = new Date(Math.max(parseDate(event.date).getTime(), week[0].date.getTime()));
          const clampedEnd = new Date(Math.min(parseDate(event.end_date).getTime(), week[6].date.getTime()));

          let startCol = clampedStart.getDay();
          let endCol = clampedEnd.getDay();
          if (weekStartsMonday) {
            startCol = (startCol + 6) % 7;
            endCol = (endCol + 6) % 7;
          }
          const spanCols = endCol - startCol + 1;
          return {
            event,
            leftPercent: (startCol / 7) * 100,
            widthPercent: (spanCols / 7) * 100,
          };
        });

        const visibleBars = bars.slice(0, 2);
        const overflowCount = bars.length > 2 ? bars.length - 2 : 0;
        const numBarRows = visibleBars.length;

        return (
          <div key={weekIdx} className="border-t border-gray-200">
            {/* Day numbers row */}
            <div className="grid grid-cols-7">
              {week.map((cell, colIdx) => {
                  const isToday = cell.date.toDateString() === today.toDateString();
                  return (
                    <div
                      key={colIdx}
                      className={`border-r border-gray-200 px-1 pt-1 pb-0.5 cursor-pointer select-none ${
                        !cell.inMonth ? "bg-gray-50" : isToday ? "bg-blue-50" : ""
                      }`}
                      onClick={() => cell.inMonth && onNavigateToDay(cell.date)}
                      onTouchStart={cell.inMonth ? (e) => handleCellTouchStart(cell.date, e) : undefined}
                      onTouchMove={cell.inMonth ? handleCellTouchMove : undefined}
                      onTouchEnd={cell.inMonth ? (e) => { e.preventDefault(); handleCellTouchEnd(cell.date, e); } : undefined}
                    >
                      <span className={`text-xs sm:text-sm font-semibold ${
                        isToday ? "text-blue-600" : cell.inMonth ? "text-gray-700" : "text-gray-400"
                      }`}>
                        {cell.date.getDate()}
                      </span>
                    </div>
                  );
                })}
            </div>

            {/* Multi-day bars zone */}
            {(numBarRows > 0 || overflowCount > 0) && (
              <div
                className="relative"
                style={{ height: numBarRows * BAR_HEIGHT + (overflowCount > 0 ? BAR_HEIGHT : 0) }}
              >
                  {visibleBars.map((bar, barIdx) => {
                    const colorOrGradient = getColorForEvent(bar.event);
                    const isGradient = colorOrGradient.startsWith("linear-gradient");
                    return (
                      <div
                        key={bar.event.id}
                        className="absolute text-[10px] rounded-sm px-1 overflow-hidden cursor-pointer hover:opacity-80 font-bold truncate"
                        style={{
                          top: barIdx * BAR_HEIGHT + 1,
                          left: `${bar.leftPercent}%`,
                          width: `calc(${bar.widthPercent}% - 2px)`,
                          height: BAR_HEIGHT - 3,
                          ...(isGradient ? { background: colorOrGradient } : { backgroundColor: colorOrGradient }),
                          ...(bar.event.canceled ? { color: '#7f1d1d', textDecoration: 'line-through' } : { color: '#fff' }),
                        }}
                        onClick={() => !bar.event._virtual && onOpenEditEvent(bar.event)}
                      >
                        {bar.event.title}
                      </div>
                    );
                  })}
                  {overflowCount > 0 && (
                    <div
                      className="absolute text-[10px] text-gray-500 cursor-pointer hover:text-gray-700 pl-1"
                      style={{ top: numBarRows * BAR_HEIGHT + 2, left: 0 }}
                      onClick={() => onNavigateToDay(week[0].date)}
                    >
                      +{overflowCount} more
                    </div>
                  )}
              </div>
            )}

            {/* Single-day events row */}
            <div className="grid grid-cols-7">
                {week.map((cell, colIdx) => {
                  if (!cell.inMonth) {
                    return <div key={colIdx} className="border-r border-gray-200 min-h-[50px] bg-gray-50" />;
                  }

                  const allEventsForDay = getEventsForDate(cell.date);
                  const singleDayEvents = allEventsForDay
                    .filter((e) => !e.end_date || e.end_date === e.date)
                    .sort((a, b) => {
                      if (a.all_day && !b.all_day) return -1;
                      if (!a.all_day && b.all_day) return 1;
                      return (a.time || "").localeCompare(b.time || "");
                    });

                  return (
                    <div
                      key={colIdx}
                      className="border-r border-gray-200 min-h-[50px] p-1 cursor-pointer hover:bg-gray-50 touch-manipulation select-none"
                      onClick={() => onNavigateToDay(cell.date)}
                      onTouchStart={(e) => handleCellTouchStart(cell.date, e)}
                      onTouchMove={handleCellTouchMove}
                      onTouchEnd={(e) => { e.preventDefault(); handleCellTouchEnd(cell.date, e); }}
                    >
                      <div className="space-y-0.5">
                        {singleDayEvents.slice(0, 2).map((event) => {
                          const colorOrGradient = getColorForEvent(event);
                          const isGradient = colorOrGradient.startsWith("linear-gradient");
                          return (
                            <div
                              key={event.id}
                              className="text-[10px] sm:text-xs p-0.5 sm:p-1 rounded truncate cursor-pointer hover:opacity-80 font-bold touch-manipulation"
                              style={{
                                ...(isGradient ? { background: colorOrGradient } : { backgroundColor: colorOrGradient }),
                                ...(event.canceled ? { color: '#7f1d1d', textDecoration: 'line-through' } : { color: '#fff' }),
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!event._virtual) onOpenEditEvent(event);
                              }}
                            >
                              {event.title}
                            </div>
                          );
                        })}
                        {singleDayEvents.length > 2 && (
                          <div
                            className="text-[10px] sm:text-xs text-gray-500 pl-1 cursor-pointer hover:text-gray-700"
                            onClick={(e) => { e.stopPropagation(); onNavigateToDay(cell.date); }}
                          >
                            +{singleDayEvents.length - 2} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MonthView;
